/**
 * Server component for dashboard layout.
 * Pre-renders static layout structure (background, grid) on the server
 * for better LCP performance.
 */

import { Suspense } from 'react';
import { DashboardLayoutClient } from './dashboard-layout-client';

/**
 * Server component that pre-renders the static layout structure.
 * The actual interactive parts (auth, navigation) are handled by the client component.
 *
 * @param children - React children to render inside the layout
 * @returns Server-rendered layout structure with background and client component slot
 *
 * @example
 * ```tsx
 * <DashboardLayoutServer>
 *   <DashboardPage />
 * </DashboardLayoutServer>
 * ```
 *
 * @see {@link ./dashboard-layout-client.tsx} for the client component implementation
 */
export function DashboardLayoutServer({ children }: { children: React.ReactNode }) {
  return (
    <div className="app-theme min-h-screen bg-background text-foreground">
      {/* Animated background elements - optimized for LCP */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
        {/* Calmed to a faint ambient glow (Linear-style flat background); the animated
            gradient blobs were toned down from opacity-50 to a subtle accent tint. */}
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-primary/10 rounded-full filter blur-3xl opacity-[0.15] will-change-transform"></div>
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-primary/10 rounded-full filter blur-3xl opacity-[0.12] will-change-transform"></div>
        {/* Grid pattern overlay - static, no animation needed */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808010_1px,transparent_1px),linear-gradient(to_bottom,#80808010_1px,transparent_1px)] bg-[size:32px_32px]"></div>
      </div>
      <div className="relative z-10">
        <Suspense fallback={null}>
          <DashboardLayoutClient>{children}</DashboardLayoutClient>
        </Suspense>
      </div>
    </div>
  );
}
